import ProgramEndedView from '@/views/ProgramEndedView.vue';
// NPM
import { createRouter, createWebHistory } from 'vue-router';

// Views
import ErrorView from '@/views/ErrorView.vue';
// Login
import LoginView from '@/views/LoginView.vue';
import ExpiredLinkView from '@/views/ExpiredLinkView.vue';
import NoAccessView from '@/views/NoAccessView.vue';
import BlockedView from '@/views/BlockedView.vue';
import TokenView from '@/views/TokenView.vue';
// Helper
// Campaigns Sign-up

import HelperStatusView from '@/views/HelperStatusView.vue';
// Relations
import RelationsLayout from '@/views/relations/RelationsLayout.vue';
import RelationsEmptyState from '@/views/relations/RelationsEmptyState.vue';
import HelperRelationOverview from '@/views/relations/HelperRelationOverview.vue';
import ReportRelationView from '@/views/relations/ReportRelationView.vue'; // Common for both helpees and helpers
import HelperReferralView from '@/views/HelperReferralView.vue';
import HelperSharingView from '@/views/HelperSharingView.vue';
// Challenges
import ChallengesLayout from '@/views/challenges/ChallengesLayout.vue';
import ChallengesAvailable from '@/views/challenges/ChallengesAvailable.vue';
import ChallengesOngoing from '@/views/challenges/ChallengesOngoing.vue';
import ChallengesValidate from '@/views/challenges/ChallengesValidate.vue';
import ChallengesRefuse from '@/views/challenges/ChallengesRefuse.vue';
import CompletedChallengesView from '@/views/challenges/CompletedChallengesView.vue';
// Events
import EventsLayout from '@/views/events/EventsLayout.vue';
import EventsAvailable from '@/views/events/EventsAvailable.vue';
import EventsOngoing from '@/views/events/EventsOngoing.vue';
import EventsValidate from '@/views/events/EventsValidate.vue';
import EventsRefuse from '@/views/events/EventsRefuse.vue';
// UGCs
import UgcsLayout from '@/views/ugcs/UgcsLayout.vue';
import UgcsAvailable from '@/views/ugcs/UgcsAvailable.vue';
import UgcsOngoing from '@/views/ugcs/UgcsOngoing.vue';
import UgcsValidate from '@/views/ugcs/UgcsValidate.vue';
import UgcsRefuse from '@/views/ugcs/UgcsRefuse.vue';
import CompletedUgcsView from '@/views/ugcs/CompletedUgcsView.vue';

// Rewards
import RewardsLayout from '@/views/rewards/RewardsLayout.vue';
import RewardsView from '@/views/rewards/RewardsView.vue';
import PurchasedRewardsView from '@/views/rewards/PurchasedRewardsView.vue';
// Settings
import SettingsLayout from '@/views/settings/SettingsLayout.vue';
import SettingsProfileView from '@/views/settings/SettingsProfileView.vue';
import SettingsTouchpointsView from '@/views/settings/SettingsTouchpointsView.vue';
import SettingsMatchingDetailsView from '@/views/settings/SettingsMatchingDetailsView.vue';
import SettingsPreference from '@/views/settings/SettingsPreference.vue';
// Helpee
// Campaigns Sign-up
import HelpeeMultiCampaignSignUpView from '@/views/HelpeeMultiCampaignSignUpView.vue';
import HelpeeCampaignSignUpView from '@/views/HelpeeCampaignSignUpView.vue';
import HelpeeSharingView from '@/views/HelpeeSharingView.vue';
import HelpeeReferralView from '@/views/HelpeeReferralView.vue';
// Relations
import HelperStartRelationView from '@/views/relations/HelperStartRelationView.vue';
import HelpeeRelationOverview from '@/views/relations/HelpeeRelationOverview.vue';
import HelpeeReportFeedbackView from '@/views/HelpeeReportFeedbackView.vue';
import HelpeeStartRelationView from '@/views/relations/HelpeeStartRelationView.vue';
import HelpeeThanksView from '@/views/HelpeeThanksView.vue';
import HelpeeValidationView from '@/views/HelpeeValidationView.vue';
import AmbassadorSignupView from '@/views/AmbassadorSignupView.vue';
import Reviews from '@/views/reviews/Reviews.vue';
import HelpeeCampaignSignUpSimpleView from '@/views/HelpeeCampaignSignUpSimpleView.vue';

import ReviewsUnique from '@/views/reviews/integrations/ReviewsUnique.vue';
import ReviewsPublic from '@/views/reviews/integrations/ReviewsPublic.vue';
import HelpeeFinish from '@/views/HelpeeFinish.vue';
import Referral from '@/views/referral/Referral.vue';
import ReferralInvitations from '@/views/referral/ReferralInvitations.vue';
import ReferralInvite from '@/views/referral/ReferralInvite.vue';
import HelpeeWaiting from '@/views/HelpeeWaiting.vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // Clean up any body classes that might interfere with scrolling
    document.body.classList.remove('overflow-hidden');

    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }
    // For all other navigation, scroll to top
    return { top: 0, behavior: 'smooth' };
  },

  routes: [
    { path: "/program-ended", name: "program-ended", component: ProgramEndedView },
    // Error Page
    {
      path: '/:pathMatch(.*)*',
      name: 'error',
      component: ErrorView,
      props: true,
    },
    // IP blocked page (public, no auth, no API calls)
    {
      path: '/blocked',
      name: 'blocked',
      component: BlockedView,
    },
    // Login
    {
      path: '/login',
      name: 'login',
      component: LoginView,
    },
    {
      // old route, redirect to new route
      path: '/sign-in/',
      redirect: () => {
        return {
          path: '/login',
        };
      },
    },
    {
      path: '/expired-link/',
      name: 'expired-link',
      component: ExpiredLinkView,
    },
    // Valid login link, but the user is no longer an ambassador (or archived)
    {
      path: '/no-access',
      name: 'no-access',
      component: NoAccessView,
    },
    {
      path: '/t/:token',
      name: 'token',
      component: TokenView,
    },
    // Helper
    // sign-up ambassadors
    {
      // legacy, to remove when sure nobody uses it, but for now we still have /:campaignUuid
      path: '/helper-signup/:campaignUuid?',
      name: 'helper-signup',
      component: AmbassadorSignupView,
    },
    {
      // also legacy, to remove when sure nobody uses it
      path: '/helper-onboarding/:campaignUuid',
      name: 'helper-onboarding',
      component: AmbassadorSignupView,
    },
    // New ambassador sign-up routes
    {
      path: '/ambassador-signup/cmpgn/:campaignUuid',
      name: 'ambassador-signup-campaign',
      component: AmbassadorSignupView,
    },
    {
      path: '/ambassador-signup/tmspc/:teamspaceUuid',
      name: 'ambassador-signup-teamspace',
      component: AmbassadorSignupView,
    },
    {
      // old route, redirect to new route
      path: '/campaign-sign-up/:campaignUuid?',
      redirect: (to) => {
        return {
          path: '/helper-signup/' + to.params.campaignUuid,
        };
      },
    },
    {
      path: '/helper-status',
      name: 'helper-status',
      component: HelperStatusView,
    },
    {
      path: '/start-relation/helper/:relationId',
      name: 'start-relation-helper',
      component: HelperStartRelationView,
    },
    {
      path: '/start-relation/helpee/:relationId',
      name: 'start-relation-helpee',
      component: HelpeeStartRelationView,
    },
    //// Relations
    {
      path: '/',
      name: 'relations',
      component: RelationsLayout,
      children: [
        {
          path: '',
          name: 'relations-empty-state',
          component: RelationsEmptyState,
        },
        {
          path: '/helper-relation/:relationId?',
          name: 'helper-relation-overview',
          component: HelperRelationOverview,
        },
      ],
    },
    {
      path: '/report/:userType/:reason/:relationId',
      name: 'report-relation-view',
      component: ReportRelationView, // Common for both helpees and helpers
    },
    {
      path: '/referral',
      name: 'referral',
      component: HelperReferralView,
    },
    {
      path: '/sharing',
      name: 'sharing',
      component: HelperSharingView,
    },
    //// Challenges
    {
      path: '/challenges',
      component: ChallengesLayout,
      children: [
        {
          path: '',
          name: 'available-challenges',
          component: ChallengesAvailable,
        },
        {
          path: 'ongoing',
          name: 'ongoing-challenges',
          component: ChallengesOngoing,
        },
        {
          path: 'validated',
          name: 'validated-challenges',
          component: ChallengesValidate,
        },
        {
          path: 'rejected',
          name: 'rejected-challenges',
          component: ChallengesRefuse,
        },
        {
          path: 'completed',
          name: 'completed-challenges',
          component: CompletedChallengesView,
        },
      ],
    },
    //// Events
    {
      path: '/events',
      component: EventsLayout,
      children: [
        { path: '', name: 'available-events', component: EventsAvailable },
        { path: 'ongoing', name: 'ongoing-events', component: EventsOngoing },
        {
          path: 'validated',
          name: 'validated-events',
          component: EventsValidate,
        },
        { path: 'rejected', name: 'rejected-events', component: EventsRefuse },
      ],
    },
    //// UGCs
    {
      path: '/ugcs',
      component: UgcsLayout,
      children: [
        { path: '', name: 'available-ugcs', component: UgcsAvailable },
        { path: 'ongoing', name: 'ongoing-ugcs', component: UgcsOngoing },
        { path: 'validated', name: 'validated-ugcs', component: UgcsValidate },
        { path: 'rejected', name: 'rejected-ugcs', component: UgcsRefuse },
        {
          path: 'completed',
          name: 'completed-ugcs',
          component: CompletedUgcsView,
        },
      ],
    },
    //// Rewards
    {
      path: '/rewards',
      component: RewardsLayout,
      children: [
        {
          path: '',
          name: 'rewards',
          component: RewardsView,
        },
        {
          path: 'purchased',
          name: 'purchased-rewards',
          component: PurchasedRewardsView,
        },
      ],
    },
    //// Settings
    {
      path: '/settings/settings-profile',
      component: SettingsLayout,
      children: [
        {
          path: '',
          name: 'settings-profile',
          component: SettingsProfileView,
        },
        {
          path: '/settings/touchpoints',
          name: 'settings-touchpoints',
          component: SettingsTouchpointsView,
        },
        {
          path: '/settings/matching-details',
          name: 'settings-matching-details',
          component: SettingsMatchingDetailsView,
        },
        {
          path: '/settings/preference',
          name: 'settings-preference',
          component: SettingsPreference,
        },
      ],
    },
    // Helpee
    //// Campaigns Sign-up
    {
      path: '/helpee-campaigns/:organizationUuid?',
      name: 'helpee-campaigns',
      component: HelpeeMultiCampaignSignUpView,
      meta: { role: 'helpee' },
    },
    {
      path: '/helpee-signup/:campaignUuid?',
      name: 'helpee-signup',
      component: HelpeeCampaignSignUpView,
      meta: { role: 'helpee' },
    },
    {
      path: '/helpee-qualification/:campaignHelpeeUuid',
      name: 'helpee-qualification',
      component: HelpeeValidationView,
      meta: { role: 'helpee' },
    },
    {
      path: '/complete-helpee-signup/:campaignHelpeeUuid',
      name: 'helpee-complete-signup',
      component: HelpeeCampaignSignUpSimpleView,
      meta: { role: 'helpee' },
    },
    {
      // old route, redirect to new route
      path: '/campaign-sign-up-form/:campaignUuid?',
      redirect: (to) => {
        return {
          path: '/helpee-signup/' + to.params.campaignUuid,
        };
      },
    },
    {
      path: '/helpee-finish',
      name: 'helpee-finish',
      component: HelpeeFinish,
    },
    {
      path: '/helpee-waiting',
      name: 'helpee-waiting',
      component: HelpeeWaiting,
    },
    {
      path: '/sharing/:shareId',
      name: 'referred-sharing',
      component: HelpeeSharingView,
      meta: { role: 'helpee' },
    },
    {
      path: '/referred/:referredUuid',
      name: 'referred',
      component: HelpeeReferralView,
      meta: { role: 'helpee' },
    },
    //// Relations
    {
      path: '/helpee-relation/:relationId?',
      name: 'helpee-relation-overview',
      component: HelpeeRelationOverview,
      meta: { role: 'helpee' },
    },
    {
      path: '/report-feedback/:type/:relationId',
      name: 'helpee-report-feedback-view',
      component: HelpeeReportFeedbackView,
      meta: { role: 'helpee' },
    },
    {
      path: '/helpee-thanks/:campaignUuid',
      name: 'helpee-thanks',
      component: HelpeeThanksView,
    },
    /// Reviews
    {
      path: '/reviews/:uuid',
      name: 'reviews',
      component: Reviews,
    },
    {
      path: '/reviews/link/:uuid',
      name: 'share-review',
      component: ReviewsUnique,
    },
    {
      path: '/reviews/public/:uuid',
      name: 'public-reviews',
      component: ReviewsPublic,
    },
    // Referral
    {
      path: '/referral',
      name: 'referral',
      component: Referral,
    },
    {
      path: '/referral/invitations',
      name: 'referral-invitations',
      component: ReferralInvitations,
    },
    {
      path: '/referral/invite',
      name: 'referral-invite',
      component: ReferralInvite,
    },
  ],
});

// Guards
// - Ensure "/login" behavior respects role separation and clears stale auth for prospects
// - Never bounce prospects to "/" when considered logged from a stale session
import { useUserStore } from '@/stores/user';
import { readJwtToken, clearJwtTokens } from '@/services/auth-tokens';
import { saveReturnTo } from '@/services/return-to';

router.beforeEach((to, _from, next) => {
  const userStore = useUserStore();
  let jwtToken: string | undefined;
  try {
    jwtToken = readJwtToken(userStore.isProspect ? 'prospect' : 'user', true);
  } catch {
    console.warn('Could not access cookies/localStorage');
  }

  // Define public routes that don't require authentication
  const publicRoutes = [
    'login',
    'expired-link',
    'no-access',
    'token',
    'helper-signup',
    'helper-onboarding',
    'ambassador-signup-campaign',
    'ambassador-signup-teamspace',
    'helpee-campaigns',
    'helpee-signup',
    'helpee-complete-signup',
    'helpee-thanks',
    'helpee-qualification',
    'helpee-finish',
    'helpee-waiting',
    'reviews',
    'public-reviews',
    'share-review',
    'referral-invite',
    'referred-sharing',
    'error',
    'blocked',
    'program-ended',
  ];

  // Check if the route requires authentication
  const requiresAuth = !publicRoutes.includes(to.name as string);

  // Special case: If route has a token query parameter, allow it to pass through
  // so App.vue can handle the authentication
  const hasTokenQuery = !!to.query.token;

  if (to.name === 'login') {
    // If logged and NOT a prospect, send to helper dashboard
    if (jwtToken && !userStore.isProspect) {
      return next({ name: 'relations-empty-state' });
    }

    // If a token exists but user is a prospect (or role unknown), keep on login and cleanup
    if (jwtToken && userStore.isProspect) {
      try {
        clearJwtTokens();
        // Clear persisted auth store if present
        localStorage.removeItem('auth');
      } catch {
        console.warn('Could not clear cookies/localStorage');
      }
    }

    return next();
  }

  // Protect authenticated routes (but allow query parameter tokens to pass through)
  if (requiresAuth && !jwtToken && !hasTokenQuery) {
    // Remember where they were heading so login can bring them back.
    saveReturnTo(to.fullPath);
    return next({ name: 'login' });
  }

  return next();
});

export default router;
